import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  Chip,
  useTheme
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { APIService } from 'src/api/api';
import { getFormattedDateTime12Hour, getFuelQuantity } from 'src/utils/getInitials';
import TankDateRangeFilter, { tankDateRangeStatePropType } from './TankDateRangeFilter';

const getRootResult = (response) => response?.result || response || {};

const getEventIcon = (type) => {
  if (type === 'refuelling') return <AddCircleOutlineIcon fontSize="small" />;
  if (type === 'draining') return <RemoveCircleOutlineIcon fontSize="small" />;
  return <CompareArrowsIcon fontSize="small" />;
};

const getEventColor = (type) => (type === 'refuelling' ? 'success' : 'error');

const SummaryPill = ({ label, value, color }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        minWidth: { xs: 'calc(50% - 6px)', sm: 156 },
        border: '1px solid',
        borderColor: alpha(theme.palette[color].main, 0.22),
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette[color].main, 0.05)
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={800} color={`${color}.dark`} sx={{ mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
};

SummaryPill.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  color: PropTypes.oneOf(['success', 'error', 'primary']).isRequired
};

const TankFuelEvents = ({ tank, dateRangeState }) => {
  const {
    timezoneName,
    dateRange,
    appliedRange,
    selectedPreset,
    validationError,
    handleDateRangeChange,
    handlePresetChange,
    handleApply,
    handleCancel,
    toUnixSeconds
  } = dateRangeState;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEnabled = Boolean(tank?.fuel_graph_enabled);

  const fetchEvents = useCallback(async () => {
    if (!tank?.id || !isEnabled) return;

    const timeBegin = toUnixSeconds(appliedRange.from);
    const timeEnd = toUnixSeconds(appliedRange.to);
    if (timeBegin === null || timeEnd === null || timeBegin >= timeEnd) return;

    setLoading(true);
    setError('');
    try {
      const response = await APIService.getTankFuelEvents(tank.id, {
        timeBegin,
        timeEnd,
        timezone: timezoneName
      });
      if (!response?.success) throw response;
      const result = getRootResult(response);
      setEvents(Array.isArray(result.events) ? result.events : []);
    } catch (requestError) {
      setEvents([]);
      setError(
        requestError?.data?.error_message
        || requestError?.error_message
        || 'Unable to load Omnicomm fuel events.'
      );
    } finally {
      setLoading(false);
    }
  }, [appliedRange, isEnabled, tank?.id, timezoneName]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const summary = useMemo(() => events.reduce((result, event) => {
    const volume = Number(event.volume) || 0;
    if (event.type === 'refuelling') {
      return {
        ...result,
        refuelling: result.refuelling + volume,
        refuellingCount: result.refuellingCount + 1
      };
    }
    if (event.type === 'draining') {
      return {
        ...result,
        draining: result.draining + volume,
        drainingCount: result.drainingCount + 1
      };
    }
    return result;
  }, {
    refuelling: 0,
    draining: 0,
    refuellingCount: 0,
    drainingCount: 0
  }), [events]);

  let tableBody;
  if (loading) {
    tableBody = (
      <TableRow>
        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading Omnicomm events
          </Typography>
        </TableCell>
      </TableRow>
    );
  } else if (events.length) {
    tableBody = events.map((event, index) => (
      <TableRow key={event.id || `${event.event_time}-${index}`} hover>
        <TableCell>
          <Chip
            icon={getEventIcon(event.type)}
            label={event.type_label || event.type}
            color={getEventColor(event.type)}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 700, textTransform: 'capitalize' }}
          />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{getFormattedDateTime12Hour(event.event_time, true) || '--'}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{getFormattedDateTime12Hour(event.start_time, true) || '--'}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{getFormattedDateTime12Hour(event.end_time, true) || '--'}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{getFuelQuantity(Number(event.volume) || 0)}</TableCell>
        <TableCell>{event.tank_number || '--'}</TableCell>
        <TableCell>{event.address || '--'}</TableCell>
      </TableRow>
    ));
  } else {
    tableBody = (
      <TableRow>
        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
          <Typography color="text.secondary">
            No refuelling or draining events found for this range.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  if (!isEnabled) {
    return (
      <Alert severity="info">
        Fuel events are available after enabling a supported Omnicomm provider and storing the tank unit id.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', lg: 'flex-start' }}
        justifyContent="space-between"
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={800}>
            Fuel Events
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Refuelling and draining activity reported directly by Omnicomm.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {`Times shown in ${timezoneName}.`}
          </Typography>
        </Box>
        <Box sx={{ width: { xs: '100%', lg: '62%' } }}>
          <TankDateRangeFilter
            dateRange={dateRange}
            selectedPreset={selectedPreset}
            validationError={validationError}
            onDateRangeChange={handleDateRangeChange}
            onPresetChange={handlePresetChange}
            onApply={handleApply}
            onCancel={handleCancel}
          />
        </Box>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
        <SummaryPill
          label={`Refuelling (${summary.refuellingCount})`}
          value={getFuelQuantity(summary.refuelling)}
          color="success"
        />
        <SummaryPill
          label={`Draining (${summary.drainingCount})`}
          value={getFuelQuantity(summary.draining)}
          color="error"
        />
        <SummaryPill label="Events" value={String(events.length)} color="primary" />
      </Stack>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: { xs: 520, md: 620 } }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Type', 'Event time', 'Start', 'End', 'Volume', 'Tank', 'Address'].map((heading) => (
                  <TableCell key={heading} sx={{ bgcolor: '#F8FAFC', fontWeight: 800, whiteSpace: 'nowrap' }}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{tableBody}</TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
};

TankFuelEvents.propTypes = {
  dateRangeState: tankDateRangeStatePropType,
  tank: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fuel_graph_enabled: PropTypes.bool
  })
};

export default TankFuelEvents;
